import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, collection, query, where, getDocs, addDoc } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, Trash2, Copy, CheckCircle, Printer, Send, MoreVertical, FileText } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';

const InventoryTransferPreview = ({ db, user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useClickOutside(() => setShowMoreMenu(false));
  const printRef = useRef();

  useEffect(() => {
    const fetchTransfer = async () => {
      try {
        const docSnap = await getDoc(doc(db, "inventory_transfers", id));
        if (docSnap.exists()) {
          setData({ id: docSnap.id, ...docSnap.data() });
        } else {
          alert("Record not found");
          navigate('/ops/inventory-transfer');
        }
      } catch (error) {
        console.error("Error fetching preview:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransfer();
  }, [db, id, navigate]);

  const handleConfirm = async () => {
    if (window.confirm("Confirm this transfer? This will lock the record.")) {
      try {
        // --- LOGIC FOR -RNT VARIANTS ---
        if (data.txnType === 'Conversion') {
          for (const comp of data.components || []) {
            const rentalSku = `${comp.sku}-RNT`;
            const productsRef = collection(db, "products");
            const q = query(productsRef, where("sku", "==", rentalSku));
            const snap = await getDocs(q);

            if (snap.empty) {
              console.log(`Creating rental variant for SKU: ${comp.sku}`);
              await addDoc(productsRef, {
                sku: rentalSku,
                basesku: comp.sku,
                name: `${comp.name || comp.description} (Rental)`,
                description: comp.description || comp.name || '',
                category: comp.category || '',
                type: comp.type || 'parts',
                uom: comp.uom || 'nos',
                brand: comp.brand || 'Nexrise',
                manufacture: comp.manufacture || 'Nexrise India',
                sac: comp.sac || '',
                image: comp.image || '',
                weight: comp.weight || 0,
                rate: 0, // Sales rate is 0 for rental variants
                rentalRate: comp.rentalRate || 0,
                inventoryvalue: comp.inventoryvalue || 0,
                isRental: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
            }
          }
        }

        await updateDoc(doc(db, "inventory_transfers", id), {
          status: 'Confirmed',
          updatedAt: serverTimestamp()
        });
        setData(prev => ({ ...prev, status: 'Confirmed' }));
        alert("Transfer confirmed successfully!");
      } catch (error) {
        alert("Error confirming: " + error.message);
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await deleteDoc(doc(db, "inventory_transfers", id));
        navigate('/ops/inventory-transfer');
      } catch (error) {
        alert("Error deleting: " + error.message);
      }
    }
  };

  const handleDuplicate = () => {
    navigate('/ops/inventory-transfer/new', { state: { duplicateData: data } });
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Transfer Slip ${data.slipNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; border: 1px solid #000; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 13px; }
            th { font-weight: bold; background-color: #f2f2f2; }
            .header-cell { font-weight: bold; background-color: #f2f2f2; width: 20%; }
            .title { text-align: center; font-size: 18px; font-weight: 800; background-color: #f1f5f9 !important; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;
  if (!data) return null;

  const totalQty = data.components?.reduce((acc, c) => acc + (Number(c.baseQty || 0) * (data.multiplier || 1)), 0) || 0;

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      {/* Header Buttons */}
      <div className="no-print" style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/inventory-transfer')} style={backBtnStyle}>
            <ArrowLeft size={20} />
          </button>
          <h2 style={{ margin: 0 }}>Transfer Slip: {data.slipNo}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {data.status === 'Draft' && (
            <>
              <button onClick={handleConfirm} style={confirmBtnStyle}>
                <Send size={18} /> Confirm Transfer
              </button>
              <button onClick={() => navigate(`/ops/inventory-transfer/edit/${id}`)} style={editBtnStyle}>
                <Edit2 size={18} /> Edit
              </button>
            </>
          )}

          {data.status === 'Confirmed' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '8px', fontWeight: 'bold' }}>
              <CheckCircle size={18} /> Confirmed
            </div>
          )}

          <button onClick={handlePrint} style={secondaryBtnStyle}>
            <Printer size={18} /> Export PDF
          </button>
          
          <div style={{ position: 'relative' }} ref={moreMenuRef}>
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} style={secondaryBtnStyle}>
              <MoreVertical size={18} />
            </button>
            {showMoreMenu && (
              <div style={dropdownStyle}>
                <button onClick={handleDuplicate} style={dropdownItemStyle} className="hover-highlight">
                  <Copy size={16} /> Duplicate
                </button>
                <button onClick={handleDelete} style={{ ...dropdownItemStyle, color: '#ef4444' }} className="hover-highlight">
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            )}
          </div>

          
        </div>
      </div>

      {/* PDF Preview Area */}
      <div ref={printRef} style={pdfContainerStyle} className="print-area">
        <table style={headerTableStyle}>
          <tbody>
            <tr><td colSpan="4" style={titleCellStyle}>INVENTORY TRANSFER SLIP</td></tr>
            <tr>
              <td style={labelCellStyle}>Slip No.</td><td style={valueCellStyle}>{data.slipNo}</td>
              <td style={labelCellStyle}>Slip Date</td><td style={valueCellStyle}>{data.slipDate}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Transaction Type</td><td style={valueCellStyle}>{data.txnType}</td>
              <td style={labelCellStyle}>Reason Code</td><td style={valueCellStyle}>{data.reasonCode}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>MOC Number</td><td style={valueCellStyle}>{data.mocNo || '---'}</td>
              <td style={labelCellStyle}>MOC Date</td><td style={valueCellStyle}>{data.mocDate || '---'}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>From Warehouse</td><td style={valueCellStyle}>{data.fromWarehouse}</td>
              <td style={labelCellStyle}>To Warehouse</td><td style={valueCellStyle}>{data.toWarehouse}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Requested By</td><td style={valueCellStyle}>{data.requestedBy}</td>
              <td style={labelCellStyle}>Department</td><td style={valueCellStyle}>{data.department}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Approved By</td><td style={valueCellStyle}>{data.approvedBy}</td>
              <td style={labelCellStyle}>Approval Date</td><td style={valueCellStyle}>{data.approvalDate}</td>
            </tr>
            <tr><td colSpan="4" style={{ height: '15px', border: '1px solid #000' }}></td></tr>
            <tr><td colSpan="4" style={labelCellStyle}>Business Justification / Why the transfer is happening?</td></tr>
            <tr><td colSpan="4" style={{ ...valueCellStyle, height: '60px', verticalAlign: 'top' }}>{data.businessJustification || '---'}</td></tr>
            <tr><td colSpan="4" style={labelCellStyle}>Delivery Challan / Pickup challan No. / Order No. / SO / RO</td></tr>
            <tr><td colSpan="4" style={{ ...valueCellStyle, height: '40px', verticalAlign: 'top' }}>{data.referenceNo || '---'}</td></tr>
          </tbody>
        </table>

        {/* Component Table */}
        <table style={pdfTableStyle}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={pdfThStyle}>Sr. No.</th>
              <th style={pdfThStyle}>From SKU</th>
              <th style={pdfThStyle}>To SKU</th>
              <th style={{ ...pdfThStyle, textAlign: 'left' }}>Description</th>
              <th style={pdfThStyle}>Qty</th>
              <th style={pdfThStyle}>UOM</th>
              <th style={pdfThStyle}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {data.components?.map((c, i) => {
              const toSku = data.txnType === 'Conversion' ? `${c.sku}-RNT` : c.sku;
              const qty = (Number(c.baseQty || 0) * (data.multiplier || 1));
              const uom = c.type === 'preset' ? 'set' : 'nos';
              return (
                <tr key={i}>
                  <td style={pdfTdStyle}>{i + 1}</td>
                  <td style={pdfTdStyle}>{c.sku}</td>
                  <td style={pdfTdStyle}>{toSku}</td>
                  <td style={{ ...pdfTdStyle, textAlign: 'left' }}>{c.description || c.name || c.sku}</td>
                  <td style={pdfTdStyle}>{qty}</td>
                  <td style={pdfTdStyle}>{uom}</td>
                  <td style={pdfTdStyle}>{c.remarks || '---'}</td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
              <td colSpan="4" style={{ ...pdfTdStyle, textAlign: 'right' }}>TOTAL</td>
              <td style={pdfTdStyle}>{totalQty}</td>
              <td colSpan="2" style={pdfTdStyle}></td>
            </tr>
          </tbody>
        </table>

        <div style={{ height: '15px' }} />

        <table style={headerTableStyle}>
          <tbody>
            <tr>
              <td style={labelCellStyle}>Dispatch Ref / Vehicle / LR No.</td><td style={valueCellStyle}>{data.dispatchRef || '---'}</td>
              <td style={labelCellStyle}>Dispatch Date</td><td style={valueCellStyle}>{data.dispatchDate}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Received By</td><td style={valueCellStyle}>{data.receivedBy || '---'}</td>
              <td style={labelCellStyle}>Received Date</td><td style={valueCellStyle}>{data.receivedDate || '---'}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Stores Verification</td><td colSpan="3" style={valueCellStyle}>{data.storesVerification}</td>
            </tr>
            <tr><td colSpan="4" style={{ height: '15px', border: '1px solid #000' }}></td></tr>
            <tr><td colSpan="4" style={labelCellStyle}>Control Checklist</td></tr>
            <tr>
              <td colSpan="4" style={valueCellStyle}>
                <div style={{ display: 'flex', gap: '30px' }}>
                  {Object.keys(data.controlChecklist || {}).map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '14px', height: '14px', border: '1px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>
                        {data.controlChecklist[key] ? '✓' : ''}
                      </div>
                      <span style={{ fontSize: '12px' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                    </div>
                  ))}
                </div>
              </td>
            </tr>
            <tr><td colSpan="4" style={{ height: '15px', border: '1px solid #000' }}></td></tr>
            <tr>
              <td style={labelCellStyle}>Prepared By</td><td style={valueCellStyle}>{data.preparedBy}</td>
              <td style={labelCellStyle}>Verified By</td><td style={valueCellStyle}>{data.verifiedBy}</td>
            </tr>
            <tr style={{ height: '60px' }}>
              <td style={labelCellStyle}>Signature</td><td style={valueCellStyle}></td>
              <td style={labelCellStyle}>Signature</td><td style={valueCellStyle}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `@media print { .no-print { display: none !important; } }`}} />
    </div>
  );
};

// --- STYLES ---
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '10px 0', borderBottom: '1px solid #e2e8f0' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const editBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const confirmBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const secondaryBtnStyle = { backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minHeight: '1100px', width: '100%', boxSizing: 'border-box', color: '#000' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '13px' };
const labelCellStyle = { ...headerTableCellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2', width: '25%' };
const valueCellStyle = { ...headerTableCellStyle, backgroundColor: '#fff' };
const titleCellStyle = { ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800', fontSize: '18px' };
const pdfTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '15px', border: '1px solid #000' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center' };

const dropdownStyle = {
  position: 'absolute', top: '100%', right: 0, marginTop: '8px',
  backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', zIndex: 10, width: '150px', overflow: 'hidden'
};

const dropdownItemStyle = {
  width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px',
  border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', textAlign: 'left'
};

export default InventoryTransferPreview;